import { bindActionCreators } from '@reduxjs/toolkit';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import type { Accessory } from '../../../@types/item';
import { track as _track, mapVehicleData } from '../../../actions/tracking';
import { asHtml } from '../../../helpers/markdown';
import { prettify } from '../../../helpers/prices';
import { cdn } from '../../../helpers/urls';
import { getItem } from '../../../selectors/item';
import { Box } from '../../../styled-components/components/Box';
import Carousel from '../../../styled-components/components/Carousel';
import { Column, Columns } from '../../../styled-components/components/Columns';
import Content from '../../../styled-components/components/Content';
import { Grid, GridItem } from '../../../styled-components/components/Grid';
import { Image } from '../../../styled-components/components/Image';
import { Link } from '../../../styled-components/components/Link';
import Modal from '../../../styled-components/components/Modal';
import { StackMedium, StackSmall } from '../../../styled-components/components/Stack';
import { Text } from '../../../styled-components/components/Text';

interface AccessoryModalProps {
  accessory: Accessory;
  onClose: () => void;
}

const AccessoryModal = ({ accessory, onClose }: AccessoryModalProps) => {
  const { t } = useTranslation(['common', 'item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const track = bindActionCreators(_track, dispatch);

  const onTrack = (parameter: string) => {
    track('accessoryShownInterest', {
      item: mapVehicleData(item),
      spreadable: {
        information: {
          id: accessory.id,
          label: accessory.name,
          context: 'productOverview',
          parameter,
        },
      },
    });
  };

  const onClickReadMore = () => onTrack('externalLinkClick');
  const onChangeImage = () => onTrack('imagePaging');

  return (
    <Modal heading={accessory.name} onClose={onClose}>
      {accessory.media?.[0]?.url && (
        <StackMedium>
          {accessory.media.length > 1 ? (
            <div className="modal-hero">
              <Carousel
                onChangeImage={onChangeImage}
                items={accessory.media.map((x, i) => ({
                  src: `${x.url}?w=570`,
                  alt: `${accessory.name} bild ${i + 1}`,
                }))}
              />
            </div>
          ) : (
            <div className="modal-hero">
              <Image src={`${accessory.media[0].url}?w=570`} alt={`${accessory.name}`} $fullWidth />
            </div>
          )}
        </StackMedium>
      )}
      {accessory.logoUrl && (
        <StackMedium>
          <Columns $spacing={2} $valign="start">
            <Column $grow>
              {false && (
                <Columns $spacing={0.5} $valign="center">
                  <Column>
                    <i className="icon-check l-block no-margin text-ui-success" />
                  </Column>
                  <Column>
                    <Text $size="small">{t('accessories.originalAccessory', { ns: 'item' })}</Text>
                  </Column>
                </Columns>
              )}
            </Column>
            <Column $noShrink>
              <Image
                src={`${cdn(accessory.logoUrl, '48x48')}&crop=fit`}
                srcSet={`${cdn(accessory.logoUrl, '96x96')}&crop=fit 2x`}
                alt={`${accessory.manufacturer} logotyp`}
              />
            </Column>
          </Columns>
        </StackMedium>
      )}
      <StackMedium>
        <Box>
          <StackSmall>
            <Columns $valign="center">
              <Column $grow>
                <Text $size="small" $weight="medium">
                  {t('priceLabel.cash', { ns: 'common' })}
                </Text>
              </Column>
              <Column $noShrink>
                <Text $size="large" $weight="bold">
                  {prettify(accessory.salePrice ? accessory.salePrice : accessory.price, 'kr')}
                </Text>
              </Column>
            </Columns>
          </StackSmall>
          {accessory.salePrice && (
            <StackSmall>
              <Columns $valign="center">
                <Column $grow>
                  <Text $size="small" $weight="light" $tone="alt">
                    {t('priceLabel.earlier', { ns: 'item' })}
                  </Text>
                </Column>
                <Column $noShrink>
                  <Text $size="small" $weight="bold" $tone="alt" $decoration="line-through">
                    {prettify(accessory.price, 'kr')}
                  </Text>
                </Column>
              </Columns>
            </StackSmall>
          )}
          {accessory.assemblyPrice && (
            <StackSmall>
              <Columns $valign="center">
                <Column $grow>
                  <Text $size="small" $weight="medium">
                    {t('accessories.installationCost', { ns: 'item' })}
                  </Text>
                </Column>
                <Column $noShrink>
                  <Text $size="large" $weight="bold">
                    {prettify(accessory.assemblyPrice, 'kr')}
                  </Text>
                </Column>
              </Columns>
            </StackSmall>
          )}
        </Box>
      </StackMedium>
      <StackMedium>
        <Content
          fullWidth
          dangerouslySetInnerHTML={{ __html: asHtml(accessory.longDescription) }}
        />
      </StackMedium>
      {(accessory.articleNumber || accessory.manufacturer) && (
        <StackMedium>
          <Grid $spacing={2} $columnsSm={2}>
            {accessory.articleNumber && (
              <GridItem>
                <StackSmall>
                  <Text $weight="medium">
                    {t('accessories.articleNumberShort', { ns: 'item' })}
                  </Text>
                </StackSmall>
                <StackSmall>{accessory.articleNumber}</StackSmall>
              </GridItem>
            )}
          </Grid>
        </StackMedium>
      )}
      {accessory.productPageLink && (
        <StackMedium>
          <Link
            onClick={onClickReadMore}
            href={accessory.productPageLink}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            {accessory.productPageLinkText || t('readMore', { ns: 'common' })}
          </Link>
        </StackMedium>
      )}
    </Modal>
  );
};

export default AccessoryModal;
